import React, { useState } from 'react';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';

export default function WorldObjectDetailModal({
  isOpen,
  onClose,
  worldObject,
  objectType, // 'character', 'place', or 'thing'
  onGenerateImage,
  isGenerating,
}) {
  const [showRawJson, setShowRawJson] = useState(false);

  if (!isOpen || !worldObject) return null;

  const name = worldObject.name || worldObject.title || 'Unknown';
  const hasImage = !!worldObject.referenceImage;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex gap-4">
              <button
                onClick={() => setShowRawJson(false)}
                className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                  !showRawJson
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Attributes
              </button>
              <button
                onClick={() => setShowRawJson(true)}
                className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                  showRawJson
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Raw JSON
              </button>
            </div>
          </div>

          {/* Attributes View */}
          {!showRawJson && (
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Name</label>
                <p className="text-sm text-gray-900">{name}</p>
              </div>

              {/* Alternate Names */}
              {worldObject.alternateNames && worldObject.alternateNames.length > 0 && (
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-2">Alternate Names</label>
                  <div className="space-y-1">
                    {worldObject.alternateNames.map((alt, idx) => (
                      <p key={idx} className="text-sm text-gray-600">• {alt}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Details - Formatted nicely */}
              {worldObject.details && typeof worldObject.details === 'object' && (
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-3">Details</label>
                  <div className="space-y-3">
                    {Object.entries(worldObject.details)
                      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                      .map(([key, value]) => {
                      const renderValue = (val) => {
                        if (val === null || val === undefined) return 'N/A';
                        if (typeof val === 'string') return val;
                        if (typeof val === 'number' || typeof val === 'boolean') return String(val);
                        if (Array.isArray(val)) {
                          return val.map((item, idx) => {
                            if (typeof item === 'string') return item;
                            if (typeof item === 'object') {
                              return Object.values(item).filter(v => typeof v === 'string').join(', ');
                            }
                            return String(item);
                          }).filter(Boolean).join('; ');
                        }
                        if (typeof val === 'object') {
                          return Object.entries(val)
                            .map(([k, v]) => `${k}: ${String(v)}`)
                            .join(', ');
                        }
                        return String(val);
                      };

                      return (
                        <div key={key} className="border-l-2 border-indigo-200 pl-3">
                          <p className="text-xs font-semibold text-indigo-700 capitalize mb-1">
                            {key.replace(/_/g, ' ')}
                          </p>
                          {typeof value === 'object' && value !== null && !Array.isArray(value) ? (
                            <div className="space-y-1">
                              {Object.entries(value).map(([subKey, subValue]) => (
                                <div key={subKey} className="text-xs">
                                  <span className="text-gray-600 font-medium capitalize">
                                    {subKey.replace(/_/g, ' ')}:
                                  </span>
                                  <p className="text-gray-700 mt-0.5">{renderValue(subValue)}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-700">{renderValue(value)}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Description */}
              {worldObject.description && typeof worldObject.description === 'string' && (
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Description</label>
                  <p className="text-sm text-gray-700">{worldObject.description}</p>
                </div>
              )}
            </div>
          )}

          {/* Raw JSON View */}
          {showRawJson && (
            <div>
              <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-96 text-gray-700">
                {JSON.stringify(worldObject, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
